import { useEffect } from 'react'
import { isOnScreen } from '../logic/openWhenReady'
import { useExportMenusStore, type ExportPrinter } from '../stores/useExportMenusStore'

/** How deep in the page `element` and `focused` part ways: the deeper, the nearer they are. */
function sharedDepth(element: HTMLElement, focused: Node): number {
  let shared: Node | null = element
  while (shared && !shared.contains(focused)) shared = shared.parentNode
  let depth = 0
  for (let up = shared; up; up = up.parentNode) depth += 1
  return depth
}

/**
 * The grid Ctrl+P prints: of the grids on screen, the one nearest the focus
 * (the grid, card or panel the person is working in shares the deepest part
 * of the page with it), else the first on the page.
 */
export function nearestPrinter(
  printers: ExportPrinter[],
  focused: Element | null
): ExportPrinter | null {
  const shown = printers
    .map((printer) => ({ printer, element: printer.element() }))
    .filter((entry): entry is { printer: ExportPrinter; element: HTMLElement } =>
      isOnScreen(entry.element)
    )
    .sort((a, b) =>
      a.element.compareDocumentPosition(b.element) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
    )
  if (shown.length === 0) return null
  if (!focused || focused === document.body) return shown[0].printer
  let nearest = shown[0]
  let nearestDepth = sharedDepth(nearest.element, focused)
  for (const entry of shown.slice(1)) {
    const depth = sharedDepth(entry.element, focused)
    if (depth > nearestDepth) {
      nearest = entry
      nearestDepth = depth
    }
  }
  return nearest.printer
}

/**
 * Ctrl+P (Cmd+P) on a page with a grid prints the grid's PDF, as its Export
 * menu's print button does, instead of the browser printing the page. With
 * no grid on screen, or a dialog open, the browser prints as usual.
 */
export function usePrintShortcut() {
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.shiftKey || event.altKey) return
      if (event.key.toLowerCase() !== 'p' || event.repeat) return
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return
      const printer = nearestPrinter(
        useExportMenusStore.getState().printers,
        document.activeElement
      )
      if (!printer) return
      event.preventDefault()
      printer.print()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])
}
